import { useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Clock } from 'lucide-react'
import { BeaconLogo } from './BeaconLogo'
import { ClockOverrideFields } from './ClockOverrideControl'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { PreviewBuildMenu } from './PreviewBuildMenu'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useClock } from '../hooks/useClock'
import { getPreviewChannel } from '../lib/ota'

interface HeaderLogoProps {
  /** Smaller mark for the floating-pill header. */
  small?: boolean
  showClock?: boolean
  /** Short day label (e.g. "THU") shown as a badge on the clock logo. */
  dayLabel?: string
}

export function HeaderLogo({ small, showClock, dayLabel }: HeaderLogoProps) {
  const size = small ? 'h-8 w-8' : 'h-11 w-11'
  const online = useOnlineStatus()
  const { now, override } = useClock()
  const { locale } = useLocaleContext()
  const [pingCount, setPingCount] = useState(0)
  const [open, setOpen] = useState(false)

  // Only changes across a bundle swap, which restarts the app — so reading it
  // once at mount is enough.
  const [previewChannel] = useState(() => getPreviewChannel())

  const hours = showClock ? now.getHours() : undefined
  const minutes = showClock ? now.getMinutes() : undefined

  // A preview bundle reads and writes real data, so its badge outranks the
  // clock badge.
  if (previewChannel) {
    return (
      <PreviewBuildMenu
        channel={previewChannel}
        logo={{ offline: !online, dayLabel, hours, minutes }}
      />
    )
  }

  if (override) {
    const label = override.toLocaleString(locale.replace(/_/g, '-'), {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="relative flex cursor-pointer rounded-full"
            aria-label={fbt('Clock pinned — change simulated time', 'Accessible label for the header logo while the clock is pinned')}
          >
            <BeaconLogo offline={!online} clockPinned dayLabel={dayLabel} hours={hours} minutes={minutes} className={size} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="size-4 shrink-0 text-amber-400" />
            <span>
              <fbt desc="Label on the banner shown when an admin has pinned the app to a fake time">
                Clock pinned to
              </fbt>{' '}
              <span className="font-medium tabular-nums">{label}</span>
            </span>
          </div>
          <ClockOverrideFields onDone={() => setOpen(false)} />
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="relative">
      {online && pingCount > 0 && (
        <span
          key={pingCount}
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-primary/40 animate-beacon-ping-once motion-reduce:hidden"
        />
      )}
      <BeaconLogo
        offline={!online}
        hours={hours}
        minutes={minutes}
        dayLabel={dayLabel}
        onClick={online ? () => setPingCount(c => c + 1) : undefined}
        className={`relative ${size}${online ? ' cursor-pointer select-none' : ''}`}
      />
    </div>
  )
}
